/**
 * Redirect Manager
 *
 * Manages redirect URLs for post-authentication flows.
 * Used to remember where users intended to go before being redirected to login/verification.
 */

const REDIRECT_KEY = 'airplx/redirect_after_auth';

/**
 * Store the URL to redirect to after successful authentication
 * @param url - The URL to redirect to (hash format: e.g., "#/settings")
 */
export function storeRedirectUrl(url: string): void {
  if (url && url !== '#/login' && url !== '#/signup' && url !== '#/verify-email') {
    localStorage.setItem(REDIRECT_KEY, url);
  }
}

/**
 * Get the stored redirect URL and clear it from storage
 * @returns The stored redirect URL, or null if none exists
 */
export function getAndClearRedirectUrl(): string | null {
  const url = localStorage.getItem(REDIRECT_KEY);
  if (url) {
    localStorage.removeItem(REDIRECT_KEY);
    return url;
  }
  return null;
}

/**
 * Get the redirect URL to use after authentication
 * @param defaultUrl - The default URL to use if no redirect is stored (default: "/")
 * @returns The URL to redirect to
 */
export function getRedirectUrl(defaultUrl: string = '/'): string {
  const storedUrl = getAndClearRedirectUrl();

  if (storedUrl) {
    // Remove leading hash if present (React Router will add it)
    return storedUrl.startsWith('#') ? storedUrl.substring(1) : storedUrl;
  }

  return defaultUrl;
}

/**
 * Clear any stored redirect URL
 */
export function clearRedirectUrl(): void {
  localStorage.removeItem(REDIRECT_KEY);
}
